/**
 * Catalog-Helper (iter106 · 2026-07-19)
 * =====================================
 * Zentraler Zugriff auf lokalisierte Kategorie-/Attribut-Labels.
 *
 * Backend liefert weiterhin nur DE-Labels — hier übersetzen wir per code.
 * Falls ein Key nicht existiert (z.B. neue Kategorie), fallen wir sauber
 * auf das mitgelieferte Backend-Label bzw. den Raw-Code zurück.
 *
 * Nutzung:
 *   const { mpCat, mpSub, stxCat, stxSub, color, material, condition,
 *           sizeLabel, onesize } = useCatalogLabels();
 *   <span>{mpCat("damen_bekleidung", node.label)}</span>
 */
import { useTranslation } from "react-i18next";

export function useCatalogLabels() {
  const { t } = useTranslation();

  return {
    /** MP-Hauptkategorie */
    mpCat: (code, fallback) => t(`catalog.mp.cat.${code}`, { defaultValue: fallback ?? code }),
    /** MP-Subkategorie (namespaced unter Parent-Code) */
    mpSub: (parentCode, subCode, fallback) =>
      t(`catalog.mp.sub.${parentCode}.${subCode}`, { defaultValue: fallback ?? subCode }),
    /** STX-Hauptkategorie */
    stxCat: (code, fallback) => t(`catalog.stx.cat.${code}`, { defaultValue: fallback ?? code }),
    /** STX-Subkategorie */
    stxSub: (parentCode, subCode, fallback) =>
      t(`catalog.stx.sub.${parentCode}.${subCode}`, { defaultValue: fallback ?? subCode }),
    /** Farbe (Wert = DE-Label als Key) */
    color: (value) => t(`catalog.attr.color.${value}`, { defaultValue: value }),
    /** Material (Wert = DE-Label als Key) */
    material: (value) => t(`catalog.attr.material.${value}`, { defaultValue: value }),
    /** Zustand (Code = NEW/VERY_GOOD/…) */
    condition: (code, fallback) =>
      t(`catalog.attr.condition.${code}`, { defaultValue: fallback ?? code }),
    /** Zustand Kurzform (Chips/Cards) */
    conditionShort: (code, fallback) => {
      if (code === "CLEAN_REQUIRED") return t("catalog.attr.condition.CLEAN_REQUIRED_SHORT");
      return t(`catalog.attr.condition.${code}`, { defaultValue: fallback ?? code });
    },
    /** Größen-Label je MP-Category */
    sizeLabel: (catCode, fallback) =>
      t(`catalog.attr.size_label.${catCode}`, { defaultValue: fallback ?? "" }),
    /** „Einheitsgröße" */
    onesize: () => t("catalog.attr.onesize"),
    /** Add-On (STX Zusatz-Optionen). Code z.B. OPT_SPORT_WORN → lokalisiertes Label */
    addon: (code, fallback) =>
      t(`catalog.attr.addon.${code}`, { defaultValue: fallback ?? code }),
    /** Öffentlicher Text (Preview für STX-Add-Ons) */
    addonPublic: (code, fallback) =>
      t(`catalog.attr.addon_public.${code}`, { defaultValue: fallback ?? code }),
  };
}
